import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../components/layout';
import { useAdminNavigation } from '../hooks/useAdminNavigation';
import styles from './TranscriptViewerPage.module.css';
import { useSearchParams } from 'react-router-dom';

const TranscriptViewerPage: React.FC = () => {
  const { sidebarCollapsed, activeSection, setSidebarCollapsed, handleNavClick, handleLogout } = useAdminNavigation();
  const [searchParams] = useSearchParams();
  const url = searchParams.get('url');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paragraphs, setParagraphs] = useState<string[] | null>(null);

  useEffect(() => {
    if (!url) return;
    let mounted = true;
    setLoading(true);
    setError(null);
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`Failed to fetch transcript: ${r.status}`);
        return r.text();
      })
      .then((txt) => {
        if (!mounted) return;
        // Normalize whitespace
        const normalized = txt.replace(/\s+/g, ' ').trim();
        // Try to split into sentences
        const sentences = normalized.match(/[^.!?]+[.!?]+/g) || [normalized];
        const groups: string[] = [];
        const chunkSize = 6;
        for (let i = 0; i < sentences.length; i += chunkSize) {
          groups.push(sentences.slice(i, i + chunkSize).join(' ').trim());
        }
        setParagraphs(groups.length ? groups : [normalized]);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(String(err));
      })
      .finally(() => mounted && setLoading(false));

    return () => { mounted = false; };
  }, [url]);

  return (
    <DashboardLayout
      collapsed={sidebarCollapsed}
      activeSection={activeSection}
      welcomeLabel={'Transcript viewer'}
      selectedRegulation={null}
      onToggleSidebar={() => setSidebarCollapsed(prev => !prev)}
      onNavClick={handleNavClick}
      onProfileClick={() => handleNavClick('profile')}
      onLogout={handleLogout}
      onRegulationModalClose={() => {}}
    >
      <section className={styles.page}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h2 className={styles.title}>Transcript viewer</h2>
            <p className={styles.subtitle}>Formatted view of the selected transcript</p>
          </div>
          <div className={styles.headerRight}>
            <a href={url ?? '#'} target="_blank" rel="noopener noreferrer" className={styles.linkButton}>Open raw</a>
          </div>
        </div>

        <div className={styles.content}>
          {loading && <div className={styles.loading}>Loading transcript…</div>}
          {error && <div className={styles.error}>Error: {error}</div>}
          {!loading && !error && paragraphs && (
            <div className={styles.viewer}>
              {paragraphs.map((p, idx) => (
                <p key={idx} className={styles.paragraph}>{p}</p>
              ))}
            </div>
          )}
          {!loading && !error && !paragraphs && (
            <div className={styles.empty}>No transcript to display</div>
          )}
        </div>
      </section>
    </DashboardLayout>
  );
};

export default TranscriptViewerPage;
